<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Chaos Sanctuary (Terror Zone) Efficient Run Guide</h1>
    <p>In <strong>Diablo II: Resurrected</strong>, when Chaos Sanctuary becomes a Terror Zone, it offers an excellent opportunity for efficient experience farming. To maximize team efficiency and level up quickly, follow these strategies.</p>

    <h2>Route Planning</h2>
    <ol>
        <li><strong>Entry Point:</strong> Start from Act 4 - Pandemonium Fortress, use the waypoint to River of Flame, and then follow the path to Chaos Sanctuary.</li>
        <li><strong>Sorceress as the Scout:</strong> A Sorceress should teleport ahead to scout and open a portal near the central area or Grand Vizier of Chaos’s spawn location to minimize travel time.</li>
        <li><strong>Farming Order:</strong> It is recommended to clear the zone clockwise or counterclockwise to ensure all elite monsters are killed for maximum experience gain.</li>
        <li><strong>Focus on Elite Monsters:</strong> Prioritize killing elite monsters (Unique & Champion monsters) since they provide significantly more experience and better loot.</li>
        <li><strong>Skip Diablo?</strong> Skipping Diablo can improve efficiency since he takes longer to kill while providing relatively lower experience.</li>
    </ol>
    <div>
      <img class="m-1 w-100" alt="Chaos Sanctuary run" src="@/assets/runs/chaos-run.png">
    </div>

    <h2>Recommended Builds</h2>
    <p>Since Chaos Sanctuary contains various elemental immunities and powerful enemies, high damage, area-clearing, and survivability-focused builds are ideal.</p>

    <ul>
        <li><strong>Lightning/Nova Sorceress (with Infinity)</strong>
            <ul>
                <li>🔹 <strong>Skills:</strong> Lightning / Chain Lightning / Nova</li>
                <li>"Infinity" breaks lightning immunities, allowing fast clearing of large monster packs.</li>
                <li>Has Teleport, making her the best option for scouting and portal setup.</li>
            </ul>
        </li>
        <li><strong>Hammerdin (Blessed Hammer Paladin)</strong>
            <ul>
                <li>🔹 <strong>Skills:</strong> Blessed Hammer + Concentration Aura</li>
                <li>Magic damage bypasses both physical and elemental immunities, making him one of the best choices for this zone.</li>
                <li>Excellent survivability with Holy Shield and high resistances.</li>
            </ul>
        </li>
        <li><strong>Fist of the Heavens Paladin (FoH Paladin)</strong>
            <ul>
                <li>🔹 <strong>Skills:</strong> Fist of the Heavens + Holy Bolt</li>
                <li>Deals high extra damage against undead and demon monsters, which make up most of Chaos Sanctuary.</li>
                <li>Fast and efficient clearing, with Holy Bolt providing additional healing support for allies.</li>
            </ul>
        </li>
        <li><strong>Mosaic Assassin (Martial Arts Assassin)</strong>
            <ul>
                <li>🔹 <strong>Skills:</strong> Phoenix Strike</li>
                <li>Massive AoE elemental damage, especially when dual-wielding "Mosaic" Rune Word claws, allowing infinite charge release.</li>
                <li>Good survivability, with Shadow Master as a tank and Fade boosting resistances and damage reduction.</li>
            </ul>
        </li>
        <li><strong>Physical Bow Amazon (Bowazon)</strong>
            <ul>
                <li>🔹 <strong>Skills:</strong> Multiple Shot / Strafe</li>
                <li>Pure physical damage ignores elemental immunities.</li>
                <li>High attack speed and piercing shots make her effective at clearing open areas in Chaos Sanctuary.</li>
            </ul>
        </li>
    </ul>

    <div id="slot-content_mpu" class="content-ad-mpu"/>

    <h2>Efficient Farming Strategies</h2>
    <ul>
        <li><strong>Form a Balanced Team</strong>
            <ul>
                <li>Ensure your team includes high-damage characters like Hammerdin, Lightning Sorceress, and FoH Paladin to maximize kill speed.</li>
            </ul>
        </li>
        <li><strong>Assign Clear Roles</strong>
            <ul>
                <li>One player should lure monsters into tight packs to increase AoE efficiency.</li>
                <li>Others should focus on high burst damage to clear groups quickly.</li>
            </ul>
        </li>
        <li><strong>Optimize Waypoint Selection</strong>
            <ul>
                <li>Upon entering Chaos Sanctuary, a Sorceress should teleport ahead, choosing the central area or Grand Vizier of Chaos’s spawn location for opening a portal.</li>
            </ul>
        </li>
        <li><strong>Prioritize Elite Monsters</strong>
            <ul>
                <li>Elite monsters (Unique & Champion Monsters) provide the most experience—focus on killing them first.</li>
                <li>Use Conviction Aura ("Infinity") or Sunder Charms to break immunities where necessary.</li>
            </ul>
        </li>
        <li><strong>Consider Skipping Diablo</strong>
            <ul>
                <li>Diablo offers less experience compared to the time spent fighting him.</li>
                <li>If your primary goal is fast XP, clear the elite monsters and reset the game instead of killing Diablo.</li>
            </ul>
        </li>
        <li><strong>Stay Alert to Avoid Wipeouts</strong>
            <ul>
                <li>High monster density and Fanaticism aura enemies make Chaos Sanctuary dangerous.</li>
                <li>Melee characters should be cautious of positioning, while ranged builds should maintain a safe distance to avoid getting swarmed.</li>
            </ul>
        </li>
    </ul>

    <h2>Important Notes</h2>
    <p>✅ Assign team roles, scout efficiently, prioritize elite monsters, and maintain a fast clearing pace.</p>

    <p>✅ Be cautious of high-damage aura enemies (Fanaticism), position carefully, and focus on team survival.</p>

    <p>✅ By following these strategies, you and your team can efficiently farm experience in the Chaos Sanctuary Terror Zone, speeding up character progression while collecting valuable loot!</p>
  </div>
</template>

<script>
import BlogList from '../BlogList.vue'

export default {
  name: 'Chaos',

  components: {
    BlogList,
  },
}
</script>